import { Link } from 'react-router-dom';
import SectionHeading from '../components/SectionHeading';
import CTAButton from '../components/CTAButton';
import Reveal from '../components/Reveal';
import { INDUSTRIES, OUR_PARTNERS, OUR_SERVICES, TECHSTACK, WHY_WORK_WITH_US } from '../constant/data';

const SERVICES   = OUR_SERVICES;
const WHY_US     = WHY_WORK_WITH_US ;
const PARTNERS   = OUR_PARTNERS;
const TECH_STACK = TECHSTACK;

function Home() {
  return (
    <>
      {/* ── Hero ── */}
      <section className="hero-section">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-6 ">
              <div className="hero-label">Based in Kampala &nbsp;·&nbsp; Serving East Africa</div>
              <h2 className="text-white display-4" style={{ textAlign: 'left' }}>Software that works.
                <br />Support that lasts.</h2>
              <p className="lead" style={{ marginBottom: '2rem', textAlign: 'left' }}>
                We build and run real technology for organisations that can't afford things to break —
                from custom software and mobile apps to web hosting, domain registration, and website design.
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-begin">
                <span className="btn-pulse-wrap">
                  <CTAButton type="gold" to="/contact">
                    Get in Touch &nbsp;<i className="fas fa-arrow-right" style={{ fontSize: '0.8rem' }} />
                  </CTAButton>
                </span>
                <CTAButton type="outline-light" to="/services">Explore Our Services</CTAButton>
              </div>
            </div>
            <div className="col-lg-6">
             <img
                src="/assets/images/developer.jpg"
                alt="Software Development in East Africa"
                style={{
                  width: '100%',
                  height: 340,
                  objectFit: 'cover',
                  borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow-md)',
                  display: 'block',
                }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <div className="features-bar">
        <div className="container">
          <div className="row g-3 text-center">
            {[
              { num: '6+',  label: 'Years building systems' },
              { num: '50+',  label: 'Projects delivered' },
              { num: '10+',  label: 'Technologies mastered' },
              { num: '50+', label: 'Happy Clients' },
            ].map((f, i) => (
              <div className="col-6 col-md-3" key={f.label}>
                <Reveal delay={i * 80}>
                  <div className="feature-item">
                    <span className="feature-num">{f.num}</span>
                    <span className="feature-label">{f.label}</span>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Partners / Trusted By ── */}
      <div className="partners-section">
        <div className="container">
          <Reveal>
            <div className="partners-label">Trusted across industries</div>
            <div className="partners-grid">
              {PARTNERS.map((p, i) => (
                <Link
                  key={p.label}
                  to="/industries"
                  className="partner-chip"
                  style={{ textDecoration: 'none', '--delay': `${i * 50}ms` }}
                >
                  <i className={p.icon} />
                  {p.label}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* ── What We Build ── */}
      <section className="section-corporate">
        <div className="container">
          <Reveal>
            <SectionHeading
              align="center"
              label="What We Do"
              subtitle="From custom applications to hosted platforms — here's the kind of work we take on."
            >
              What We Build
            </SectionHeading>
          </Reveal>

          <div className="row g-3">
            {SERVICES.map((s, i) => (
              <div className="col-lg-3 col-md-4 col-6" key={s.title}>
                <Reveal delay={i * 60}>
                  <div className="service-card" style={{ padding: '1.5rem' }}>
                    <div className="service-icon"><i className={s.icon} /></div>
                    <h4 style={{ fontSize: '0.92rem' }}>{s.title}</h4>
                    <p style={{ fontSize: '0.82rem', margin: 0 }}>{s.desc}</p>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>

          <Reveal>
            <div className="text-center mt-4">
              <Link to="/services" className="btn btn-outline-navy">
                See all services &nbsp;<i className="fas fa-arrow-right" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className="techstack-section">
        <div className="container">
          <Reveal>
            <div className="text-center mb-4">
              <div className="section-label" style={{ display: 'block', marginBottom: '0.4rem' }}>Built With</div>
              <h2 style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.75rem)', fontWeight: 700, color: 'var(--ink-1)', marginBottom: 0 }}>
                Technologies we work with
              </h2>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <div className="techstack-grid">
              {TECH_STACK.map((t, i) => (
                <div key={t.name} className="tech-chip">
                  <i className={t.icon} />
                  <span>{t.name}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Why Us ── */}
      <section className="section-corporate">
        <div className="container">
          <Reveal>
            <SectionHeading
              align="center"
              label="Why Teams Work With Us"
              subtitle="We're not the cheapest option — but we show up, we care, and our systems actually work."
            >
              What makes us different
            </SectionHeading>
          </Reveal>

          <div className="row g-4">
            {WHY_US.map((w, i) => (
              <div className="col-lg-3 col-md-6" key={w.title}>
                <Reveal delay={i * 80}>
                  <div className="trust-card">
                    <div className="trust-card-icon"><i className={w.icon} /></div>
                    <h5>{w.title}</h5>
                    <p>{w.body}</p>
                  </div>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="section-corporate" style={{ background: 'var(--surface-2)', borderTop: '1px solid var(--border-1)', borderBottom: '1px solid var(--border-1)' }}>
        <div className="container">
          <Reveal>
            <SectionHeading align="center" label="Who We Work With">
              Industries
            </SectionHeading>
          </Reveal>

          <Reveal delay={100}>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {INDUSTRIES.map(ind => (
                <Link
                  key={ind.title}
                  to="/industries"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    border: '1.5px solid var(--border-1)',
                    borderRadius: 8,
                    padding: '0.75rem 1.25rem',
                    color: 'var(--ink-2)',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    textDecoration: 'none',
                    background: 'var(--card-bg)',
                    transition: 'all 0.22s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--ink-1)'; e.currentTarget.style.color = 'var(--surface-1)'; e.currentTarget.style.borderColor = 'var(--ink-1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--card-bg)'; e.currentTarget.style.color = 'var(--ink-2)'; e.currentTarget.style.borderColor = 'var(--border-1)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <i className={ind.icon} />
                  {ind.title}
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container text-center position-relative">
          <Reveal>
            <div
              style={{
                display: 'inline-block',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 50,
                padding: '0.3rem 1rem',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
                marginBottom: '1.5rem',
              }}
            >
              Let's work together
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.6rem)' }}>
              Let's talk about what you need
            </h2>
            <p>
              Tell us what you're dealing with. We'll tell you honestly whether we can help
              and what that would look like. No pitch, no pressure.
            </p>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              <CTAButton type="gold" to="/contact">
                Start Your Project &nbsp;<i className="fas fa-arrow-right" style={{ fontSize: '0.78rem' }} />
              </CTAButton>
              <CTAButton type="outline-light" to="/about">Learn About Us</CTAButton>
            </div>
            <div style={{ marginTop: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
              {[
                { icon: 'fas fa-bolt', text: 'Reply within 24 hours' },
                { icon: 'fas fa-shield-halved', text: 'Confidential by default' },
                { icon: 'fas fa-map-marker-alt', text: 'Based in Kampala' },
              ].map(f => (
                <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>
                  <i className={f.icon} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  {f.text}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default Home;
